import * as d3 from 'd3';
import $ from 'jquery';

import * as contextmenu from 'components/ContextMenu';
import SummaryScrollbar from 'molbio/maps/alignment/summary/SummaryScrollbar';
import SummarySliceView, {
  SummarySliceViewInitializationOptions,
} from 'molbio/maps/alignment/summary/SummarySliceView';
import Component from 'molbio/maps/linear/Component';
import {ScrollData} from 'molbio/maps/linear/InfiniteScroller';
import tooltip from 'molbio/maps/TooltipTemplate';

export default class SummarySliceLineView<TRowState> extends SummarySliceView<TRowState> {
  lazyStart!: number;
  lazyEnd!: number;
  scrollBar?: SummaryScrollbar;
  intId!: number | null;
  $tooltip!: JQuery;

  initialize(options: SummarySliceViewInitializationOptions<TRowState>, ...otherArgs: Array<unknown>): void {
    this.lazyStart = 0;
    this.lazyEnd = 0;
    // @ts-expect-error: spreading unknown args - we expect them to be undefined actually
    super.initialize(options, ...otherArgs);
  }

  constructor(options: SummarySliceViewInitializationOptions<TRowState>) {
    super(options);
  }

  forEachVisibleView(fn: (component: Component, index: number) => void): void {
    this._doOnRange(Math.max(this.lazyStart, 0), Math.min(this.lazyEnd, this.listComponents.length), fn);
  }

  getScrollData(): ScrollData | null {
    return this.tabstate.get('scrollData');
  }

  adjustHeight(): void {
    const h = this.tabstate.getSummaryMapLineLayout().boxHeight();

    let boxHeight = h * this.lazyStart;
    this.forEachVisibleView((component: Component, index: number): void => {
      d3.select(component.el).attr('transform', `translate(0,${boxHeight})`);
      boxHeight += h;
    });

    boxHeight = h * this.listComponents.length;
    d3.select(this.el).attr('height', boxHeight + this.marginBottom);
    this.lineLayer.select('rect.action-rect').attr('height', boxHeight);
    this.triggerHeightChange(boxHeight);
  }

  _doOnRange(start: number, end: number, action: (component: Component, index: number) => void): void {
    for (let index = start; index < end; index++) {
      action(this.listComponents[index], index);
    }
  }

  reorder(): void {
    // Remove all old views
    this.forEachVisibleView((view) => {
      view.remove();
    });
    [this.lazyStart, this.lazyEnd] = [0, 0];
    // Reorder componnets
    super.reorder();
    // Rerender components
    this.showVerticalSlice(0, 200);
  }

  renderScrollbar(): void {
    const boxHeight = this.tabstate.getSummaryMapLineLayout().boxHeight();
    const rect = d3
      .select(this.el)
      .append('svg:g')
      .classed('summary-scrollbar', true)
      .append('svg:rect')
      .attr('fill', 'white')
      .attr('fill-opacity', 0.6)
      .attr('stroke', 'black')
      .attr('stroke-width', 2);
    this.scrollBar = new SummaryScrollbar({
      el: rect.node(),
      // @ts-expect-error
      slice: this.slice,
      range: this.range,
      model: this.getScrollData()!,
      fullHeight: () => boxHeight * this.listComponents.length,
    });

    const getPos = (e: JQueryEventObject): number => {
      const y = d3.pointer(e, this.el)[1];
      const scrollData = this.getScrollData()!;
      const visible = scrollData.get('endPosition') - scrollData.get('position') + 1;
      return this.scrollBar!.posFromY(y) - visible / 2;
    };

    const getBP = (e: JQueryEventObject): number => {
      const x = d3.pointer(e, this.el)[0];
      const xInv = this.slice.xInv();
      return xInv(x);
    };

    d3.select(this.el)
      .on('dblclick', (e: JQueryEventObject) => {
        const pos = getPos(e);
        this.getScrollData()!.setPosition(pos);
      })
      .on('contextmenu', (e: JQueryEventObject) => {
        const pos = getPos(e);
        const bp = getBP(e);
        contextmenu.view.open(e, [
          {
            name: 'Scroll Here',
            click: () => {
              this.getScrollData()!.setPosition(pos);
              this.range.pan(bp);
            },
          },
        ]);
      });

    this.scrollBar.render();
  }

  die(): void {
    super.die();
    this.$tooltip.remove();
    clearTimeout(this.intId!);
  }

  renderTooltip(): void {
    // @ts-expect-error: Vendored types expect the pixel measurements to be numbers
    this.$tooltip = $(tooltip({top: '0px', left: '-1000px', text: ''}));
    this.intId = null;
    $(document.body).append(this.$tooltip);

    // Closured variables
    const {$tooltip} = this;
    let oldIndex: number | null = null;
    let index: number | null = null;
    let tooltipX: number | null = null;
    let tooltipY: number | null = null;
    let tooltipName: string | null = null;

    const clearTooltip = (): void => {
      $tooltip.css({left: -1000});
      clearTimeout(this.intId!);
      oldIndex = null;
    };

    const showTooltip = (): void => {
      // Reset css for later fade in.
      $tooltip.removeClass('in').addClass('out').removeClass('fade');

      clearTimeout(this.intId!);
      // @ts-expect-error: The Node types declare that this returns a Timeout object but really it is expected to return a number
      this.intId = setTimeout(() => {
        // Update info
        // @ts-expect-error: vendored types expect numbers instead of strings for pixel measurements
        $tooltip.css({left: tooltipX, top: tooltipY});
        $tooltip.find('.tooltip-inner').text(tooltipName!);

        // Fade in.
        $tooltip.removeClass('out').addClass('in').addClass('fade');
      }, 500);
    };

    const computeTooltip = (e: JQueryEventObject): void => {
      const mouseLoc = d3.pointer(e, this.el);
      const x = mouseLoc[0];
      [tooltipX, tooltipY] = d3.pointer(e, document.body);

      // top
      const height = this.tabstate.getSummaryMapLineLayout().boxHeight();
      // D3 has bug where y is sometimes reported as negative when zoomed in.
      const y = Math.max(mouseLoc[1], 0);
      index = Math.min(Math.floor(y / height), this.listComponents.length - 1);
      const targetY = index * height + 3;
      tooltipY += targetY - y;
      tooltipY -= $tooltip.height() + 5;

      // left
      const targetX = x;
      tooltipX += targetX - x;
      tooltipX -= $tooltip.width() / 2;

      // name
      // @ts-expect-error: model should be either SummaryMapSingleState or ProteinAlignmentRow corresponding to the tabstate
      tooltipName = this.tabstate.getNameFromSubmapstate(this.listComponents[index].model);
    };

    d3.select('.summary-scrollbar').on('mousemove', clearTooltip);
    this.lineLayer.on('mouseleave', clearTooltip);
    this.lineLayer.on('mousemove', (e: JQueryEventObject) => {
      computeTooltip(e);
      if (index !== oldIndex) {
        showTooltip();
      }
      oldIndex = index;
    });
  }

  render(): this {
    super.render();
    if (this.getScrollData()) {
      this.renderScrollbar();
    }
    this.renderTooltip();
    return this;
  }

  showVerticalSlice(top: number, bottom: number): void {
    const h = this.tabstate.getSummaryMapLineLayout().boxHeight();
    const [oLazyStart, oLazyEnd] = [this.lazyStart, this.lazyEnd];
    this.lazyStart = Math.max(Math.floor(top / h), 0);
    this.lazyEnd = Math.min(Math.ceil(bottom / h), this.listComponents.length);

    // detach things that do not need to be rendered.
    // render things that do need to be rendered.
    // update things that should now be there.

    const removeAction = (view: Component): void => {
      view.remove();
    };
    const renderAction = (view: Component): void => {
      const g = this.sublineLayer.insert('svg:g').node();
      view.setElement(g);
      view.render(false);
    };

    function updateAction(view: Component): void {} // view.update(false) # noop, it should already be fine.

    if (oLazyStart < this.lazyStart) {
      if (oLazyEnd < this.lazyStart) {
        // os oe s e
        this._doOnRange(oLazyStart, oLazyEnd, removeAction);
        this._doOnRange(this.lazyStart, this.lazyEnd, renderAction);
      } else if (oLazyEnd < this.lazyEnd) {
        // os s oe e
        this._doOnRange(oLazyStart, this.lazyStart, removeAction);
        this._doOnRange(this.lazyStart, oLazyEnd, updateAction);
        this._doOnRange(oLazyEnd, this.lazyEnd, renderAction);
      } else {
        // os s e oe
        this._doOnRange(oLazyStart, this.lazyStart, removeAction);
        this._doOnRange(this.lazyStart, this.lazyEnd, updateAction);
        this._doOnRange(this.lazyEnd, oLazyEnd, removeAction);
      }
    } else if (oLazyStart < this.lazyEnd) {
      if (oLazyEnd < this.lazyEnd) {
        // s os oe e
        this._doOnRange(this.lazyStart, oLazyStart, renderAction);
        this._doOnRange(oLazyStart, oLazyEnd, updateAction);
        this._doOnRange(oLazyEnd, this.lazyEnd, renderAction);
      } else {
        // s os e oe
        this._doOnRange(this.lazyStart, oLazyStart, renderAction);
        this._doOnRange(oLazyStart, this.lazyEnd, updateAction);
        this._doOnRange(this.lazyEnd, oLazyEnd, removeAction);
      }
    } else {
      // s e os oe
      this._doOnRange(this.lazyStart, this.lazyEnd, renderAction);
      this._doOnRange(oLazyStart, oLazyEnd, removeAction);
    }

    this.adjustAndRange();
  }
}
